import {
    validateIncorporatedRequirementDispositions,
    type IncorporatedAuthoritySnapshotV1,
    type IncorporatedRequirementDispositionReceiptV1,
} from './incorporated-authority-snapshot.js';
import {
    clientBoundaryTextProjectsIncorporatedRequirement,
    sourceBoundarySubjectForLineage,
} from './incorporated-lineage-semantics.js';

type LooseRecord = Record<string, unknown>;

function record(value: unknown): LooseRecord | null {
    return value && typeof value === 'object' && !Array.isArray(value)
        ? value as LooseRecord
        : null;
}

function validatedAuthority(projectWMS: unknown): Readonly<{
    snapshot: IncorporatedAuthoritySnapshotV1;
    receipt: IncorporatedRequirementDispositionReceiptV1;
}> | null {
    const root = record(projectWMS);
    const structured = record(root?.structured) || root;
    const snapshot = record(structured?.incorporated_technical_authority_snapshot);
    const receipt = record(structured?.incorporated_requirement_disposition_receipt);
    if (!snapshot || !receipt || !Array.isArray(receipt.dispositions)) return null;

    const validation = validateIncorporatedRequirementDispositions(
        snapshot as unknown as IncorporatedAuthoritySnapshotV1,
        receipt.dispositions,
    );
    if (!validation.ok) return null;
    const canonical = validation.receipt;
    if (receipt.version !== canonical.version
        || receipt.kind !== canonical.kind
        || receipt.complete !== canonical.complete
        || receipt.snapshotFingerprint !== canonical.snapshotFingerprint
        || receipt.requirementCount !== canonical.requirementCount
        || receipt.dispositionCount !== canonical.dispositionCount
        || receipt.dispositionFingerprint !== canonical.dispositionFingerprint) return null;
    return {
        snapshot: snapshot as unknown as IncorporatedAuthoritySnapshotV1,
        receipt: canonical,
    };
}

/**
 * Derive client-facing exclusions only from a validated immutable snapshot and
 * its matching disposition receipt. Unresolved OCR predicates remain visible
 * in the Stage 3 audit register but emit no invented client wording.
 */
function derivedBoundaryRows(projectWMS: unknown): Array<Readonly<{
    requirementText: string;
    exclusion: string;
}>> {
    const authority = validatedAuthority(projectWMS);
    if (!authority) return [];
    const requirementsById = new Map(
        authority.snapshot.requirements.map(requirement => [requirement.id, requirement]),
    );
    const rows: Array<Readonly<{ requirementText: string; exclusion: string }>> = [];
    for (const disposition of authority.receipt.dispositions) {
        const requirement = requirementsById.get(disposition.requirement_id);
        if (!requirement || !requirement.byOthers || requirement.sourceBoundary !== 'by_others') continue;
        const subject = sourceBoundarySubjectForLineage(requirement.text)
            .replace(/[\s,;:.]+$/g, '')
            .trim();
        if (!subject) continue;
        if (rows.some(existing => (
            clientBoundaryTextProjectsIncorporatedRequirement(requirement.text, existing.exclusion)
        ))) continue;
        const next = {
            requirementText: requirement.text,
            exclusion: `${subject} — excluded from this quote; by others.`,
        };
        // Prefer one complete aggregate boundary over earlier narrower rows;
        // this keeps derivation order-independent without losing packages.
        for (let index = rows.length - 1; index >= 0; index -= 1) {
            if (clientBoundaryTextProjectsIncorporatedRequirement(
                rows[index]!.requirementText,
                next.exclusion,
            )) rows.splice(index, 1);
        }
        rows.push(next);
    }
    return rows;
}

export function deriveIncorporatedBoundaryExclusions(projectWMS: unknown): string[] {
    return derivedBoundaryRows(projectWMS).map(row => row.exclusion);
}

/** Preserve builder-authored exclusions while adding only validated boundaries. */
export function mergeIncorporatedBoundaryExclusions(
    quoteContent: unknown,
    projectWMS: unknown,
): LooseRecord {
    const quote = record(quoteContent) || {};
    const derived = derivedBoundaryRows(projectWMS);
    if (derived.length === 0) return quote;
    const existing = record(quote.exclusions);
    const existingItems = Array.isArray(existing?.items)
        ? existing.items.map(value => String(value || '').trim()).filter(Boolean)
        : Array.isArray(quote.exclusions)
            ? quote.exclusions.map(value => String(value || '').trim()).filter(Boolean)
            : typeof quote.exclusions === 'string' && quote.exclusions.trim()
                ? [quote.exclusions.trim()]
                : [];
    const additions = derived
        .filter(row => !existingItems.some(existing => (
            clientBoundaryTextProjectsIncorporatedRequirement(row.requirementText, existing)
        )))
        .map(row => row.exclusion);
    if (additions.length === 0) return quote;
    return {
        ...quote,
        exclusions: {
            title: String(existing?.title || 'Exclusions'),
            ...(existing || {}),
            items: [...new Set([...existingItems, ...additions])],
        },
    };
}
